import NumberFlow from '@number-flow/react';
import { InfoIcon } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { Card } from 'src/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { ConfigurationReference } from 'src/features/configuration/configurationReferences/ConfigurationReference';
import { cn } from 'src/lib/utils';
import { useSessionProductsQuery } from 'src/services/api/getOpenSessionProducts';

type ConfigurationReferencesProps = {
  sessionId: string;
};

export function ConfigurationReferences({
  sessionId,
}: ConfigurationReferencesProps): ReactElement {
  const [topShadow, setTopShadow] = useState(true);
  const [bottomShadow, setBottomShadow] = useState(true);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { data, isFetching } = useSessionProductsQuery(sessionId);
  const { t } = useTranslation('configuration');

  return (
    <>
      <span
        className={cn(
          'absolute top-2 right-1 flex items-center gap-2',
          isFetching && 'opacity-50',
        )}
      >
        <span className='flex items-center gap-1 font-medium text-layout-foreground-secondary text-sm'>
          <Tooltip>
            <TooltipTrigger className='flex items-center gap-1 focus:outline-hidden'>
              <NumberFlow value={data.length} />
              <InfoIcon className='size-3' />
            </TooltipTrigger>
            <TooltipContent className='max-w-xs'>
              <p className='flex flex-col gap-1 font-normal text-sm'>
                <span>
                  {t(
                    'The number of products in the list may not match the sum of the products of the selected criteria',
                  )}
                </span>
                <span>
                  {t(
                    'This is because some products may be associated with multiple criteria',
                  )}
                </span>
              </p>
            </TooltipContent>
          </Tooltip>
        </span>
      </span>
      <Card
        className={cn(
          'relative flex flex-1 flex-col gap-2 overflow-hidden',
          isFetching && 'opacity-50',
        )}
      >
        <div
          className={cn(
            'flex flex-1 flex-col gap-2 overflow-y-auto pr-4 pb-2 pl-6',
            topShadow && !bottomShadow && 'mask-fade-top',
            bottomShadow && !topShadow && 'mask-fade-bottom',
            topShadow && bottomShadow && 'mask-fade',
          )}
          ref={setScrollRoot}
        >
          <InView
            aria-hidden='true'
            as='div'
            className='h-px shrink-0'
            onChange={(inView) => setTopShadow(!inView)}
            root={scrollRoot}
          />
          {data.map((product) => (
            <ConfigurationReference
              criteria={product.criteria ?? []}
              key={product.sku}
              name={product.title}
              sessionId={sessionId}
              sku={product.sku}
            />
          ))}
          {data.length === 0 && (
            <div className='flex flex-1 items-center justify-center'>
              <p className='text-layout-foreground-secondary text-sm'>
                {t('No products in the list')}
              </p>
            </div>
          )}
          <InView
            aria-hidden='true'
            as='div'
            className='h-px shrink-0'
            onChange={(inView) => setBottomShadow(!inView)}
            root={scrollRoot}
          />
        </div>
      </Card>
    </>
  );
}
